import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import { Redirect, useHistory, useParams } from "react-router-dom";
import {
  FaCamera,
  FaEdit,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRedo,
  FaSchool,
  FaSyncAlt,
  FaUserFriends,
  FaUserGraduate,
  FaUserPlus,
} from "react-icons/fa";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/StudentDetails.css";

const fmt = (v) => {
  if (v == null || String(v).trim() === "") return "—";
  return String(v);
};

//Created By Cursor
function DetailField({ label, children, className = "" }) {
  return (
    <div className={`sr-field std-ro-field${className ? ` ${className}` : ""}`}>
      <span className="sr-field__label">{label}</span>
      <span className="sr-input std-ro-value">{children}</span>
    </div>
  );
}

//Created By Cursor
function SectionCard({ icon: Icon, title, subtitle, step, children, extra }) {
  return (
    <section className="sr-section stur-section stur-section--compact">
      <div className="sr-section__head stur-section__head">
        <span className="stur-section__step" aria-hidden>
          {step || (Icon ? <Icon /> : null)}
        </span>
        <div className="min-w-0">
          <h2 className="sr-section__title">{title}</h2>
          {subtitle ? <p className="sr-section__sub">{subtitle}</p> : null}
        </div>
        {extra ? <div className="sr-section__extra">{extra}</div> : null}
      </div>
      <div className="sr-section__body">{children}</div>
    </section>
  );
}

/** JPEG export from canvas — toBlob + toDataURL fallback (Safari / security). */
function canvasToJpegFile(canvas, baseName) {
  const finish = (blob) => {
    if (!blob || blob.size === 0) {
      throw new Error("Could not encode image");
    }
    return new File([blob], `${baseName}_rotated.jpg`, { type: "image/jpeg" });
  };

  return new Promise((resolve, reject) => {
    const fallback = () => {
      try {
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        const m = /^data:image\/jpeg;base64,(.+)$/.exec(dataUrl);
        if (!m) {
          reject(new Error("Could not encode image"));
          return;
        }
        const bin = atob(m[1]);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(finish(new Blob([arr], { type: "image/jpeg" })));
      } catch (err) {
        reject(err);
      }
    };

    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            try {
              resolve(finish(blob));
            } catch (e) {
              reject(e);
            }
          } else {
            fallback();
          }
        },
        "image/jpeg",
        0.92
      );
    } else {
      fallback();
    }
  });
}

/** Rotate image file by 0 / 90 / 180 / 270 so upload matches preview. */
function rotateImageFile(file, degrees) {
  const normalized = ((degrees % 360) + 360) % 360;
  if (normalized === 0) return Promise.resolve(file);

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) {
        reject(new Error("Invalid image dimensions"));
        return;
      }
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (normalized === 90 || normalized === 270) {
        canvas.width = h;
        canvas.height = w;
      } else {
        canvas.width = w;
        canvas.height = h;
      }
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((normalized * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2);

      const base =
        file.name.replace(/\.[^.]+$/, "").replace(/_rotated$/, "") || "photo";
      try {
        const outFile = await canvasToJpegFile(canvas, base);
        resolve(outFile);
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Could not encode image"));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

function resolveImageUrl(path) {
  const p = String(path || "").trim();
  if (!p) throw new Error("No image URL");
  if (/^https?:\/\//i.test(p)) return p;
  const base = (process.env.REACT_APP_SERVER_NAME || "").replace(/\/$/, "");
  if (base && p.startsWith("/")) {
    return `${base}${p}`;
  }
  try {
    return new URL(p, window.location.origin).href;
  } catch {
    return p;
  }
}

/**
 * Load an already-hosted image and rotate for re-upload.
 * Prefer same-origin API proxy (avoids CORS on static image URLs from React dev / different ports).
 */
async function rotateImageFromUrl(imageUrl, degrees, options = {}) {
  const normalized = ((degrees % 360) + 360) % 360;
  if (normalized === 0) {
    throw new Error("No rotation");
  }
  const { enrollmentNo, schoolCode, cacheBust } = options;

  const tryProxyBlob = async () => {
    if (!enrollmentNo || !schoolCode) return null;
    const base =
      (process.env.REACT_APP_SERVER_NAME || "") +
      (process.env.REACT_APP_TARGET_DIR || "");
    let proxyUrl = `${base}getStudentImageBlob.php?enrollment_no=${encodeURIComponent(
      enrollmentNo
    )}&school_code=${encodeURIComponent(schoolCode)}`;
    if (cacheBust != null && String(cacheBust) !== "") {
      proxyUrl += `&v=${encodeURIComponent(String(cacheBust))}`;
    }
    const { data } = await axios.get(proxyUrl, {
      responseType: "blob",
      withCredentials: true,
    });
    if (!(data instanceof Blob) || data.size === 0) {
      return null;
    }
    if (
      data.type.startsWith("image/") ||
      !data.type ||
      data.type === "application/octet-stream"
    ) {
      return data;
    }
    const txt = await data.text();
    try {
      const j = JSON.parse(txt);
      throw new Error(j.api_remark || j.message || "Could not load photo");
    } catch (e) {
      if (e instanceof SyntaxError) {
        throw new Error("Could not load photo");
      }
      throw e;
    }
  };

  const tryAxiosBlob = async () => {
    const { data } = await axios.get(imageUrl, {
      responseType: "blob",
      withCredentials: true,
    });
    if (!(data instanceof Blob) || data.size === 0) {
      throw new Error("Empty image");
    }
    return data;
  };

  const tryFetchBlob = async () => {
    let res = await fetch(imageUrl, { credentials: "include" });
    if (!res.ok) {
      res = await fetch(imageUrl, { mode: "cors", credentials: "omit" });
    }
    if (!res.ok) {
      throw new Error("Could not load photo");
    }
    return res.blob();
  };

  const tryCanvasFromImage = (useCors) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (useCors) img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext("2d").drawImage(img, 0, 0);
          canvas.toBlob((b) => {
            if (b) resolve(b);
            else reject(new Error("Could not read photo"));
          }, "image/png");
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error("Image load failed"));
      img.src = imageUrl;
    });

  let blob = await tryProxyBlob().catch(() => null);

  if (!blob) {
    try {
      blob = await tryAxiosBlob();
    } catch {
      try {
        blob = await tryFetchBlob();
      } catch (primary) {
        try {
          blob = await tryCanvasFromImage(false);
        } catch {
          try {
            blob = await tryCanvasFromImage(true);
          } catch {
            throw primary instanceof Error ? primary : new Error("Load failed");
          }
        }
      }
    }
  }

  const fallbackMime = blob.type && blob.type.startsWith("image/")
    ? blob.type
    : "image/jpeg";
  const ext = fallbackMime.includes("png")
    ? "png"
    : fallbackMime.includes("webp")
      ? "webp"
      : "jpg";
  const file = new File([blob], `student.${ext}`, { type: fallbackMime });
  return rotateImageFile(file, degrees);
}

function parseUploadApiResponse(data) {
  if (data == null) return null;
  if (typeof data === "object" && !Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function admissionLabel(id) {
  const s = id == null ? "" : String(id).trim();
  if (s === "ADMTYPE01") return "Re-admission";
  if (s === "ADMTYPE02") return "New admission";
  return s || "—";
}

function absImg(path) {
  const p = String(path || "").trim();
  if (!p || p === "—" || p.endsWith("/")) return "";
  try {
    return resolveImageUrl(p);
  } catch {
    return p;
  }
}

export default function StudentDetails() {
  const history = useHistory();
  const { student_id, session_id } = useParams();
  const loggedIn = localStorage.getItem("token") != null;
  const photoInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [mStudentDetails, setStudentDetails] = useState([]);
  const [mclass, setClass] = useState([]);
  const [mContactDetails, setContactDetails] = useState([]);
  const [mFamilyDetails, setFamilyDetails] = useState([]);
  const [mGuardians, setGuardians] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [photoRotationDeg, setPhotoRotationDeg] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [photoRevision, setPhotoRevision] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setPhotoRotationDeg(0);
    setSelectedFile(null);
    setPreviewURL(null);
    setPhotoRevision(0);
  }, [student_id, session_id]);

  const loadDetails = useCallback(
    async (cancelToken) => {
      setLoading(true);
      setStudentDetails([]);
      setClass([]);
      setContactDetails([]);
      setFamilyDetails([]);
      setGuardians([]);
      try {
        const schoolCode = SessionManager.getSchoolCode();
        const params = {
          school_code: schoolCode,
          enrollment_no: student_id,
          session_id,
          aadhar_no: "",
          _t: refreshKey,
        };
        const url = `${API_BASE()}searchStudentDetails.php`;
        console.log("Full URL: ", `${url}?${new URLSearchParams(params)}`);
        const response = await axios
          .get(url, {
            params,
            cancelToken,
            headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK" && response.data?.length) {
          const data = response.data;
          setStudentDetails(data);
          setClass(data[0]?.class || []);
          setContactDetails(data[0]?.contact_details || []);
          setFamilyDetails(data[0]?.family_details || []);
          setGuardians(data[0]?.gaurdian_details || []);
        } else {
          if (response?.api_remark) myToast.notify_warning(response.api_remark);
        }
      } catch (error) {
        if (axios.isCancel?.(error)) return;
        console.error(error);
        myToast.notify_warning("Failed to load student details.");
      } finally {
        setLoading(false);
      }
    },
    [student_id, session_id, refreshKey]
  );

  useEffect(() => {
    const cancelSource = axios.CancelToken.source();
    loadDetails(cancelSource.token);
    return () => cancelSource.cancel("StudentDetails request cancelled");
  }, [loadDetails]);

  const goToUpdate = () => {
    if (!mStudentDetails.length) return;
    localStorage.setItem("STDetails", JSON.stringify(mStudentDetails));
    history.push("/AdminMain/UpdateStudentDetails");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      myToast.notify_warning("Please select a file.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      myToast.notify_warning("Please select a valid image file.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedFile(file);
      setPreviewURL(reader.result);
      setPhotoRotationDeg(0);
    };
    reader.onerror = () => myToast.notify_warning("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    const details = mStudentDetails[0];
    const enroll = details?.student_enrollment_no;
    const imgPath = details?.student_img_path?.trim();
    const hasUploadedPhoto = Boolean(imgPath);
    const rot = ((photoRotationDeg % 360) + 360) % 360;
    const uploadNewFile = Boolean(selectedFile);
    const uploadRotatedExisting =
      !uploadNewFile && hasUploadedPhoto && rot !== 0;

    if (!uploadNewFile && !uploadRotatedExisting) {
      myToast.notify_warning(
        hasUploadedPhoto
          ? "Rotate the photo, then click Upload."
          : "Please select an image first."
      );
      return;
    }

    setUploading(true);
    const staffData = SessionManager.getStaffDetails();
    const staff_id = staffData?.staff_id;
    if (!staff_id || !enroll) {
      setUploading(false);
      myToast.notify_warning("Missing staff or student ID");
      return;
    }

    let fileToUpload;
    try {
      if (uploadNewFile) {
        fileToUpload =
          rot !== 0 ? await rotateImageFile(selectedFile, rot) : selectedFile;
      } else {
        const absUrl = resolveImageUrl(imgPath);
        const fetchUrl = `${absUrl}${absUrl.includes("?") ? "&" : "?"}v=${refreshKey}`;
        fileToUpload = await rotateImageFromUrl(fetchUrl, rot, {
          enrollmentNo: enroll,
          schoolCode: SessionManager.getSchoolCode() || "",
          cacheBust: refreshKey,
        });
      }
    } catch (e) {
      console.error(e);
      setUploading(false);
      myToast.notify_warning(
        e instanceof Error && e.message
          ? e.message
          : "Could not prepare the image."
      );
      return;
    }

    const formData = new FormData();
    formData.append("image", fileToUpload);
    formData.append("enrollment_no", enroll);
    formData.append("staff_id", staff_id);
    try {
      const response = await axios.post(
        `${API_BASE()}uploadStudentPic.php`,
        formData
      );
      const payload = parseUploadApiResponse(response.data);
      if (payload?.api_status === "OK") {
        myToast.notify_success(payload.api_remark || "Image uploaded.");
        const classDetails = details.class?.[0] || {};
        const sid = details.student_enrollment_no;
        const sess = classDetails.session_id || session_id;
        setSelectedFile(null);
        setPreviewURL(null);
        setPhotoRotationDeg(0);
        setPhotoRevision((n) => n + 1);
        setRefreshKey((k) => k + 1);
        const targetPath = `/AdminMain/StudentDetails/${sid}/${sess}`;
        if (history.location.pathname !== targetPath) {
          history.push(targetPath);
        }
      } else {
        myToast.notify_warning(payload?.api_remark || "Upload failed");
      }
    } catch (error) {
      console.error(error);
      const errPayload = parseUploadApiResponse(error.response?.data);
      myToast.notify_warning(
        errPayload?.api_remark || errPayload?.message || "Image upload failed."
      );
    } finally {
      setUploading(false);
    }
  };

  const student = mStudentDetails[0];
  const cls = mclass?.[0];
  const family = useMemo(() => {
    const f = mFamilyDetails?.[0] || {};
    if (!student) return f;
    return {
      father_name: f.father_name || student.father_name,
      father_occupation: f.father_occupation || student.father_occupation,
      father_qualification: student.father_qualification,
      father_aadhar: student.father_aadhar,
      father_img_path: f.father_img_path || student.father_img_path,
      mother_name: f.mother_name || student.mother_name,
      mother_occupation: f.mother_occupation || student.mother_occupation,
      mother_qualification: student.mother_qualification,
      mother_aadhar: student.mother_aadhar,
      mother_img_path: f.mother_img_path || student.mother_img_path,
      email_id: f.email_id || student.email_id,
    };
  }, [mFamilyDetails, student]);

  const classLabel = useMemo(() => {
    if (!cls) return "—";
    const parts = [cls.class_name, cls.section_name].filter(Boolean);
    return parts.join(" · ") || "—";
  }, [cls]);

  const hasUploadedPhoto = Boolean(student?.student_img_path?.trim());
  const canRotatePhoto = Boolean(selectedFile || hasUploadedPhoto);
  const rotationApplied = ((photoRotationDeg % 360) + 360) % 360 !== 0;
  const canUploadPhoto =
    Boolean(selectedFile) || (hasUploadedPhoto && rotationApplied);
  const busy = loading || uploading;

  if (!loggedIn) return <Redirect to="/Login" />;

  const photoSrc =
    previewURL ||
    (student?.student_img_path
      ? `${student.student_img_path}${
          student.student_img_path.includes("?") ? "&" : "?"
        }v=${refreshKey}&r=${photoRevision}`
      : "");
  const hasPhotoPreview = Boolean(photoSrc);

  return (
    <div
      className="content payroll-page sm-page sr-page stur-page std-details"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Students Details</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaUserGraduate />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Students Details</h1>
              <p className="sm-hero__sub">
                {student
                  ? `${fmt(student.stu_name)} · ${classLabel} · ${fmt(
                      cls?.session_name || session_id
                    )}`
                  : "Loading student profile…"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={() => setRefreshKey((k) => k + 1)}
              disabled={busy}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            {student ? (
              <button
                type="button"
                className="sr-btn sr-btn--primary std-edit-btn"
                onClick={goToUpdate}
                disabled={busy}
              >
                <FaEdit aria-hidden /> Edit
              </button>
            ) : null}
          </div>
        </header>

        <div className="sr-body stur-body">
          {loading && !student ? (
            <div className="std-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !student ? (
            <div className="std-empty">
              <div className="std-empty__circle">
                <FaUserGraduate aria-hidden />
              </div>
              <p className="std-empty__title">No student found</p>
              <p className="std-empty__hint">
                No record for this enrollment and session.
              </p>
            </div>
          ) : null}

          {student ? (
            <>
              <section className="std-profile stur-profile">
                <div className="stur-photo-wrap std-photo-wrap">
                  <button
                    type="button"
                    className={`stur-photo${
                      hasPhotoPreview ? " stur-photo--has" : ""
                    }`}
                    onClick={() => photoInputRef.current?.click()}
                    aria-label="Choose student photo"
                    disabled={busy}
                  >
                    {hasPhotoPreview ? (
                      <img
                        key={`avatar-${refreshKey}-${photoRevision}`}
                        className="stur-photo__img"
                        style={
                          rotationApplied && (selectedFile || hasUploadedPhoto)
                            ? { transform: `rotate(${photoRotationDeg}deg)` }
                            : undefined
                        }
                        src={photoSrc}
                        alt=""
                      />
                    ) : (
                      <span className="stur-photo__empty">
                        <FaCamera className="stur-photo__icon" aria-hidden />
                        <span className="stur-photo__title">Student photo</span>
                        <span className="stur-photo__hint">Click to choose</span>
                      </span>
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </div>

                <div className="std-profile__main">
                  <h2 className="std-profile__name">
                    {fmt(student.stu_name)}{" "}
                    <span className="std-sdo">S/D/O</span>{" "}
                    {fmt(family.father_name)}
                  </h2>
                  <p className="std-profile__sub">
                    {classLabel}
                    {cls?.student_roll_no != null &&
                    String(cls.student_roll_no) !== ""
                      ? ` · Roll ${fmt(cls.student_roll_no)}`
                      : ""}
                  </p>
                  <div className="stur-badge-row">
                    <span className="stur-badge">
                      ID: {fmt(student.student_enrollment_no)}
                    </span>
                    {student.sr_admission_no ? (
                      <span className="stur-badge stur-badge--soft">
                        SRN: {fmt(student.sr_admission_no)}
                      </span>
                    ) : null}
                    {student.gender ? (
                      <span className="stur-badge stur-badge--soft">
                        {fmt(student.gender)}
                      </span>
                    ) : null}
                    {cls?.admission_type_id ? (
                      <span className="stur-badge stur-badge--soft">
                        {admissionLabel(cls.admission_type_id)}
                      </span>
                    ) : null}
                  </div>
                  <div className="stur-photo__actions std-profile__actions">
                    <button
                      type="button"
                      className="stur-photo__btn"
                      onClick={() => photoInputRef.current?.click()}
                      disabled={busy}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      className="stur-photo__btn stur-photo__btn--ghost"
                      onClick={() => setPhotoRotationDeg((d) => (d + 90) % 360)}
                      disabled={!canRotatePhoto || busy}
                      title="Rotate 90°"
                    >
                      <FaRedo aria-hidden /> Rotate
                    </button>
                    <button
                      type="button"
                      className="stur-photo__btn"
                      onClick={handleUpload}
                      disabled={!canUploadPhoto || busy}
                    >
                      {uploading ? "Uploading…" : "Upload"}
                    </button>
                  </div>
                </div>
              </section>

              <SectionCard
                icon={FaSchool}
                step="01"
                title="Academic"
                subtitle="Class, session and admission"
              >
                <div className="stur-basic">
                  <div className="stur-basic__group stur-basic__group--class">
                    <div className="stur-basic__grid stur-basic__grid--3">
                      <DetailField label="Class">
                        {fmt(cls?.class_name)}
                      </DetailField>
                      <DetailField label="Section">
                        {fmt(cls?.section_name)}
                      </DetailField>
                      <DetailField label="Roll no.">
                        {fmt(cls?.student_roll_no)}
                      </DetailField>
                      <DetailField label="Session">
                        {fmt(cls?.session_name || session_id)}
                      </DetailField>
                      <DetailField label="Board">
                        {fmt(cls?.board_name)}
                      </DetailField>
                      <DetailField label="Medium">
                        {fmt(cls?.medium_name)}
                      </DetailField>
                      <DetailField label="Admission type">
                        {admissionLabel(cls?.admission_type_id)}
                      </DetailField>
                      <DetailField label="Registration date">
                        {fmt(cls?.registration_date || student.admission_date)}
                      </DetailField>
                      <DetailField label="Class status">
                        {fmt(cls?.status)}
                      </DetailField>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                icon={FaIdCard}
                step="02"
                title="Personal & identity"
                subtitle="Demographics and ID numbers"
              >
                <div className="stur-basic">
                  <div className="stur-basic__group stur-basic__group--identity">
                    <div className="stur-basic__group-head">
                      <span className="stur-basic__group-title">Identity</span>
                      <span className="stur-basic__group-line" aria-hidden />
                    </div>
                    <div className="stur-basic__grid stur-basic__grid--4">
                      <DetailField label="Student name">
                        {fmt(student.stu_name)}
                      </DetailField>
                      <DetailField label="Gender">
                        {fmt(student.gender)}
                      </DetailField>
                      <DetailField label="Date of birth">
                        {fmt(student.birthday)}
                      </DetailField>
                      <DetailField label="Aadhaar no.">
                        {fmt(student.adharcard_no)}
                      </DetailField>
                      <DetailField label="Enrollment no.">
                        {fmt(student.student_enrollment_no)}
                      </DetailField>
                      <DetailField label="SRN">
                        {fmt(student.sr_admission_no)}
                      </DetailField>
                      <DetailField label="PEN">
                        {fmt(student.student_pen)}
                      </DetailField>
                      <DetailField label="APAAR">
                        {fmt(student.student_apaar)}
                      </DetailField>
                      <DetailField
                        label={
                          fmt(student.state_specific_id_name) !== "—"
                            ? student.state_specific_id_name
                            : "State ID"
                        }
                      >
                        {fmt(student.state_specific_id_value)}
                      </DetailField>
                      <DetailField label="Email">
                        {fmt(student.email_id)}
                      </DetailField>
                    </div>
                  </div>
                  <div className="stur-basic__group stur-basic__group--demographics">
                    <div className="stur-basic__group-head">
                      <span className="stur-basic__group-title">
                        Demographics
                      </span>
                      <span className="stur-basic__group-line" aria-hidden />
                    </div>
                    <div className="stur-basic__grid stur-basic__grid--4">
                      <DetailField label="Blood group">
                        {fmt(student.bloodgroup)}
                      </DetailField>
                      <DetailField label="Religion">
                        {fmt(student.religion)}
                      </DetailField>
                      <DetailField label="Nationality">
                        {fmt(student.nationality)}
                      </DetailField>
                      <DetailField label="Community">
                        {fmt(student.community)}
                      </DetailField>
                      <DetailField label="Caste">
                        {fmt(student.caste)}
                      </DetailField>
                      <DetailField label="Mother tongue">
                        {fmt(student.mother_tongue)}
                      </DetailField>
                    </div>
                  </div>
                  <div className="stur-basic__group stur-basic__group--physical">
                    <div className="stur-basic__group-head">
                      <span className="stur-basic__group-title">
                        Physical & other
                      </span>
                      <span className="stur-basic__group-line" aria-hidden />
                    </div>
                    <div className="stur-basic__grid stur-basic__grid--4">
                      <DetailField label="Height">
                        {fmt(student.height)}
                      </DetailField>
                      <DetailField label="Weight">
                        {fmt(student.weight)}
                      </DetailField>
                      <DetailField label="BPL beneficiary">
                        {fmt(student.bpl_beneficiary)}
                      </DetailField>
                      <DetailField label="Physical disability">
                        {fmt(student.physical_disability)}
                      </DetailField>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                icon={FaMapMarkerAlt}
                step="03"
                title="Address"
                subtitle="Locality and residence"
              >
                <div className="stur-basic">
                  <div className="stur-basic__group stur-basic__group--adm-ids">
                    <div className="stur-basic__group-head">
                      <span className="stur-basic__group-title">Locality</span>
                      <span className="stur-basic__group-line" aria-hidden />
                    </div>
                    <div className="stur-basic__grid stur-basic__grid--3">
                      <DetailField label="Village / Town">
                        {fmt(student.village_town)}
                      </DetailField>
                      <DetailField label="Post / Locality">
                        {fmt(student.post_locality)}
                      </DetailField>
                      <DetailField label="Police station">
                        {fmt(student.police_station)}
                      </DetailField>
                    </div>
                  </div>
                  <div className="stur-basic stur-address-panels">
                    <div className="stur-basic__group stur-basic__group--addr-current">
                      <div className="stur-basic__group-head">
                        <span className="stur-basic__group-title">Current</span>
                        <span className="stur-basic__group-line" aria-hidden />
                      </div>
                      <DetailField
                        label="Current address"
                        className="std-ro-field--wide"
                      >
                        {fmt(student.current_address)}
                      </DetailField>
                    </div>
                    <div className="stur-basic__group stur-basic__group--addr-permanent">
                      <div className="stur-basic__group-head">
                        <span className="stur-basic__group-title">
                          Permanent
                        </span>
                        <span className="stur-basic__group-line" aria-hidden />
                      </div>
                      <DetailField
                        label="Permanent address"
                        className="std-ro-field--wide"
                      >
                        {fmt(student.permanent_address)}
                      </DetailField>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                icon={FaUserFriends}
                step="04"
                title="Parents / family"
                subtitle="Father and mother details"
              >
                <div className="stur-basic std-family-grid">
                  <div className="stur-basic__group stur-basic__group--family-father std-family-card">
                    <div className="stur-basic__group-head">
                      <span className="stur-basic__group-title">Father</span>
                      <span className="stur-basic__group-line" aria-hidden />
                    </div>
                    <div className="std-family-card__body">
                      <div className="std-family-photo-wrap">
                        {absImg(family.father_img_path) ? (
                          <img
                            className="std-family-photo"
                            src={absImg(family.father_img_path)}
                            alt=""
                          />
                        ) : (
                          <div className="std-family-photo std-family-photo--empty">
                            <FaUserGraduate aria-hidden />
                          </div>
                        )}
                      </div>
                      <div className="stur-basic__grid stur-basic__grid--2 std-family-fields">
                        <DetailField label="Father name">
                          {fmt(family.father_name)}
                        </DetailField>
                        <DetailField label="Occupation">
                          {fmt(family.father_occupation)}
                        </DetailField>
                        <DetailField label="Qualification">
                          {fmt(family.father_qualification)}
                        </DetailField>
                        <DetailField label="Aadhaar">
                          {fmt(family.father_aadhar)}
                        </DetailField>
                      </div>
                    </div>
                  </div>
                  <div className="stur-basic__group stur-basic__group--family-mother std-family-card">
                    <div className="stur-basic__group-head">
                      <span className="stur-basic__group-title">Mother</span>
                      <span className="stur-basic__group-line" aria-hidden />
                    </div>
                    <div className="std-family-card__body">
                      <div className="std-family-photo-wrap">
                        {absImg(family.mother_img_path) ? (
                          <img
                            className="std-family-photo"
                            src={absImg(family.mother_img_path)}
                            alt=""
                          />
                        ) : (
                          <div className="std-family-photo std-family-photo--empty">
                            <FaUserGraduate aria-hidden />
                          </div>
                        )}
                      </div>
                      <div className="stur-basic__grid stur-basic__grid--2 std-family-fields">
                        <DetailField label="Mother name">
                          {fmt(family.mother_name)}
                        </DetailField>
                        <DetailField label="Occupation">
                          {fmt(family.mother_occupation)}
                        </DetailField>
                        <DetailField label="Qualification">
                          {fmt(family.mother_qualification)}
                        </DetailField>
                        <DetailField label="Aadhaar">
                          {fmt(family.mother_aadhar)}
                        </DetailField>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                icon={FaUserPlus}
                step="05"
                title="Guardians"
                subtitle="Additional guardian contacts"
              >
                {mGuardians?.length > 0 ? (
                  <div className="stur-basic std-family-grid">
                    {mGuardians.map((g, index) => {
                      const tone =
                        index % 3 === 0
                          ? "stur-basic__group--contact-1"
                          : index % 3 === 1
                            ? "stur-basic__group--contact-2"
                            : "stur-basic__group--contact-email";
                      return (
                        <div
                          className={`stur-basic__group ${tone} std-family-card`}
                          key={g.gaurdian_id || index}
                        >
                          <div className="stur-basic__group-head">
                            <span className="stur-basic__group-title">
                              Guardian {index + 1}
                            </span>
                            <span
                              className="stur-basic__group-line"
                              aria-hidden
                            />
                          </div>
                          <div className="std-family-card__body">
                            <div className="std-family-photo-wrap">
                              {absImg(g.gaurdian_img_path) ? (
                                <img
                                  className="std-family-photo"
                                  src={absImg(g.gaurdian_img_path)}
                                  alt=""
                                />
                              ) : (
                                <div className="std-family-photo std-family-photo--empty">
                                  <FaUserPlus aria-hidden />
                                </div>
                              )}
                            </div>
                            <div className="stur-basic__grid stur-basic__grid--2 std-family-fields">
                              <DetailField label="Name">
                                {fmt(g.gaurdian_name)}
                              </DetailField>
                              <DetailField label="Relation">
                                {fmt(g.gaurdian_relation)}
                              </DetailField>
                              <DetailField label="Contact">
                                {fmt(g.gaurdian_contact_no)}
                              </DetailField>
                              <DetailField label="Aadhaar">
                                {fmt(g.gaurdian_aadhar)}
                              </DetailField>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="std-contact-empty">
                    No guardian details on file.
                  </p>
                )}
              </SectionCard>

              <SectionCard
                icon={FaPhoneAlt}
                step="06"
                title="Contact details"
                subtitle="Phone numbers and relations"
              >
                {mContactDetails?.length > 0 ? (
                  <div className="stur-basic std-contact-list">
                    {mContactDetails.map((item, index) => {
                      const tone =
                        index % 2 === 0
                          ? "stur-basic__group--contact-1"
                          : "stur-basic__group--contact-2";
                      return (
                        <div
                          className={`stur-basic__group ${tone} std-contact-card`}
                          key={index}
                        >
                          <div className="stur-basic__group-head">
                            <span className="stur-basic__group-title">
                              Contact {index + 1}
                            </span>
                            <span
                              className="stur-basic__group-line"
                              aria-hidden
                            />
                          </div>
                          <div className="stur-basic__grid stur-basic__grid--4">
                            <DetailField label={`Phone ${index + 1}`}>
                              {fmt(item.contact_number)}
                            </DetailField>
                            <DetailField label="Contact person">
                              {fmt(item.contact_person_name)}
                            </DetailField>
                            <DetailField label="Relation">
                              {fmt(item.contact_person_relation)}
                            </DetailField>
                            <DetailField label="WhatsApp">
                              {item.is_whatsapp_number === true ||
                              item.is_whatsapp_number === 1 ||
                              String(item.is_whatsapp_number).toLowerCase() ===
                                "yes"
                                ? "Yes"
                                : item.is_whatsapp_number == null ||
                                    String(item.is_whatsapp_number).trim() ===
                                      ""
                                  ? "—"
                                  : "No"}
                            </DetailField>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="std-contact-empty">
                    No contact details on file.
                  </p>
                )}
              </SectionCard>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
